import React, { useEffect, useState, useContext } from 'react';
import { format } from 'date-fns';
import orderBy from 'lodash/orderBy';

import { db } from '../../firebase';
import { AuthContext } from '../../Providers/UserProvider';
import { rejectionMessage } from '../../utils/rejectionMessage';
import { MISSING_COMPANY_CLAIM_ERROR, companyIdFromClaims } from '../../utils/companyClaim';
import LoaderComponent from '../../Shared/Loader';
import { TechProject } from '../ProjectDetailPage';
import ProjectResults from './ProjectResults';

function loadAlertMessage(error: unknown, fallback: string): string {
    const detail = rejectionMessage(error, '');
    return detail ? `${fallback} ${detail}` : fallback;
}

/**
 * A started project stays current until it has a real end date before today.
 * Missing and blank end dates fail `endDate >= today`, which used to drop them
 * from Current. They are not a finished day, so they stay on this list.
 */
function endDateStillCurrent(endDate: unknown, today: string): boolean {
    if (typeof endDate !== 'string' || endDate.trim() === '') return true;
    return endDate >= today;
}

const ProjectsListPage: React.FC = () => {
    const { tech, user }: any = useContext(AuthContext);
    const [currentTechProjects, setCurrentTechProjects] = useState([] as TechProject[]);
    const [pastTechProjects, setPastTechProjects] = useState([] as TechProject[]);
    const [futureTechProjects, setFutureTechProjects] = useState([] as TechProject[]);
    const [currentLoading, setCurrentLoading] = useState(true);
    const [pastLoading, setPastLoading] = useState(true);
    const [futureLoading, setFutureLoading] = useState(true);
    const [currentError, setCurrentError] = useState('');
    const [pastError, setPastError] = useState('');
    const [futureError, setFutureError] = useState('');
    const [today, setToday] = useState(() => format(new Date(), 'yyyy-MM-dd'));

    useEffect(() => {
        let midnightTimer: ReturnType<typeof setTimeout> | undefined;

        const syncToday = () => {
            const next = format(new Date(), 'yyyy-MM-dd');
            setToday((current) => (current === next ? current : next));
        };

        const scheduleMidnight = () => {
            if (midnightTimer !== undefined) clearTimeout(midnightTimer);
            const now = new Date();
            const nextMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0, 0);
            midnightTimer = setTimeout(() => {
                syncToday();
                scheduleMidnight();
            }, nextMidnight.getTime() - now.getTime());
        };

        const onVisibilityChange = () => {
            if (document.visibilityState !== 'visible') return;
            syncToday();
            scheduleMidnight();
        };

        document.addEventListener('visibilitychange', onVisibilityChange);
        scheduleMidnight();

        return () => {
            document.removeEventListener('visibilitychange', onVisibilityChange);
            if (midnightTimer !== undefined) clearTimeout(midnightTimer);
        };
    }, []);

    useEffect(() => {
        const companyId = companyIdFromClaims(user?.claims);
        if (!companyId) {
            setPastTechProjects([]);
            setPastError(MISSING_COMPANY_CLAIM_ERROR);
            setPastLoading(false);
            return undefined;
        }
        return db
            .collection(`/regions/${user.claims.regionId}/techProjects`)
            .where('companyId', '==', companyId)
            .where('techId', '==', tech?.id)
            .where('endDate', '<', today)
            .onSnapshot(
                (projectSnapshot) => {
                    setPastError('');
                    setPastTechProjects(
                        orderBy(
                            projectSnapshot.docs
                                .map((doc) => ({ ...doc.data(), id: doc.id } as TechProject))
                                // endDate < today also matches blank and null; those stay in Current.
                                .filter((proj) => !endDateStillCurrent(proj.endDate, today)),
                            ['startDate'],
                            ['desc'],
                        ),
                    );
                    setPastLoading(false);
                },
                (error) => {
                    console.error('Unable to load past projects', error);
                    setPastTechProjects([]);
                    setPastError(loadAlertMessage(error, 'Past projects could not load.'));
                    setPastLoading(false);
                },
            );
    }, [user, today, tech]);

    useEffect(() => {
        const companyId = companyIdFromClaims(user?.claims);
        if (!companyId) {
            setFutureTechProjects([]);
            setFutureError(MISSING_COMPANY_CLAIM_ERROR);
            setFutureLoading(false);
            return undefined;
        }
        return db
            .collection(`/regions/${user.claims.regionId}/techProjects`)
            .where('companyId', '==', companyId)
            .where('techId', '==', tech?.id)
            .where('startDate', '>', today)
            .orderBy('startDate', 'desc')
            .onSnapshot(
                (projectSnapshot) => {
                    setFutureError('');
                    setFutureTechProjects(
                        projectSnapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id } as TechProject)),
                    );
                    setFutureLoading(false);
                },
                (error) => {
                    console.error('Unable to load upcoming projects', error);
                    setFutureTechProjects([]);
                    setFutureError(loadAlertMessage(error, 'Upcoming projects could not load.'));
                    setFutureLoading(false);
                },
            );
    }, [user, today, tech]);

    useEffect(() => {
        const companyId = companyIdFromClaims(user?.claims);
        if (!companyId) {
            setCurrentTechProjects([]);
            setCurrentError(MISSING_COMPANY_CLAIM_ERROR);
            setCurrentLoading(false);
            return undefined;
        }
        return db
            .collection(`/regions/${user.claims.regionId}/techProjects`)
            .where('companyId', '==', companyId)
            .where('techId', '==', tech.id)
            .where('startDate', '<=', today)
            .orderBy('startDate', 'desc')
            .onSnapshot(
                (projectSnapshot) => {
                    setCurrentError('');
                    setCurrentTechProjects(
                        projectSnapshot.docs
                            .map((doc) => ({ ...doc.data(), id: doc.id } as TechProject))
                            .filter((proj) => endDateStillCurrent(proj.endDate, today)),
                    );
                    setCurrentLoading(false);
                },
                (error) => {
                    console.error('Unable to load current projects', error);
                    setCurrentTechProjects([]);
                    setCurrentError(loadAlertMessage(error, 'Current projects could not load.'));
                    setCurrentLoading(false);
                },
            );
    }, [user, today, tech]);

    return (
        <div className="p-4">
            <div className="container mx-auto sm:px-4">
                <div className="mb-12">
                    <h2 className="text-2xl pl-4">Current</h2>
                    {currentLoading ? (
                        <LoaderComponent showLoader />
                    ) : currentError ? (
                        <p
                            className="text-red-600 text-sm text-center p-2"
                            role="alert"
                            data-testid="projects-current-error"
                        >
                            {currentError}
                        </p>
                    ) : (
                        <ProjectResults techProjects={currentTechProjects} />
                    )}
                </div>
                <div className="mb-12">
                    <h2 className="text-2xl pl-4">Upcoming</h2>
                    {futureLoading ? (
                        <LoaderComponent showLoader />
                    ) : futureError ? (
                        <p
                            className="text-red-600 text-sm text-center p-2"
                            role="alert"
                            data-testid="projects-upcoming-error"
                        >
                            {futureError}
                        </p>
                    ) : (
                        <ProjectResults techProjects={futureTechProjects} />
                    )}
                </div>
                <div className="mb-12">
                    <h2 className="text-2xl pl-4">Past</h2>
                    {pastLoading ? (
                        <LoaderComponent showLoader />
                    ) : pastError ? (
                        <p
                            className="text-red-600 text-sm text-center p-2"
                            role="alert"
                            data-testid="projects-past-error"
                        >
                            {pastError}
                        </p>
                    ) : (
                        <ProjectResults techProjects={pastTechProjects} />
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProjectsListPage;
